<script setup lang="ts">
import { computed, onUnmounted } from 'vue';
import BeforeAfterSlider from '@/components/Tools/BeforeAfterSlider.vue';
import { Button } from '@/components/ui/button';

const props = withDefaults(
    defineProps<{
        originalFile: File;
        resultBlob: Blob;
        resultFilename: string;
        comparison?: boolean;
        /** When true, omit Download / Process another (e.g. actions live in the options column). */
        hideActions?: boolean;
    }>(),
    { comparison: false, hideActions: false },
);

const emit = defineEmits<{
    (e: 'reset'): void;
}>();

const originalUrl = URL.createObjectURL(props.originalFile);
const resultUrl = URL.createObjectURL(props.resultBlob);

onUnmounted(() => {
    URL.revokeObjectURL(originalUrl);
    URL.revokeObjectURL(resultUrl);
});

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return bytes + ' B';
    }

    if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB';
    }

    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const originalSize = computed(() => formatSize(props.originalFile.size));
const resultSize = computed(() => formatSize(props.resultBlob.size));
const savingsPct = computed(() => {
    if (props.originalFile.size === 0) {
        return null;
    }

    const pct = Math.round(
        ((props.originalFile.size - props.resultBlob.size) /
            props.originalFile.size) *
            100,
    );

    return pct;
});
</script>

<template>
    <div class="space-y-4">
        <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
            <!-- Before/after slider or plain result image -->
            <BeforeAfterSlider
                v-if="comparison"
                :before-src="originalUrl"
                :after-src="resultUrl"
            />
            <img
                v-else
                :src="resultUrl"
                class="mx-auto block max-h-80 w-auto rounded-lg object-contain"
                alt="Result"
            />

            <!-- Size stats -->
            <div
                class="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm"
            >
                <span class="text-gray-500 dark:text-gray-400">
                    {{ originalSize }}
                    <span class="mx-1 text-gray-400">→</span>
                    <span
                        :class="
                            savingsPct !== null && savingsPct > 0
                                ? 'font-semibold text-green-600 dark:text-green-400'
                                : 'font-semibold text-gray-700 dark:text-gray-300'
                        "
                    >
                        {{ resultSize }}
                    </span>
                    <span
                        v-if="savingsPct !== null && savingsPct > 0"
                        class="ml-1 text-xs text-green-600 dark:text-green-400"
                    >
                        ({{ savingsPct }}% smaller)
                    </span>
                </span>
                <span
                    class="truncate text-xs text-gray-400 dark:text-gray-500"
                    >{{ resultFilename }}</span
                >
            </div>
        </div>

        <div v-if="!hideActions" class="flex gap-2">
            <a
                :href="resultUrl"
                :download="resultFilename"
                class="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
            >
                Download
            </a>
            <Button variant="outline" class="flex-1" @click="emit('reset')">
                Process another
            </Button>
        </div>
    </div>
</template>
